import classNames from "classnames/bind";
import styles from "./Footer.module.css";
import { Button } from "../Button/Button";
import { NewsletterForm } from "./NewsletterForm";
import Image from "next/image";
import Script from "next/script";
import { NLXLogo } from "../icons/NLXLogo";

const cx = classNames.bind(styles);

export interface LinkProps {
  name: string;
  href: string;
  newWindow: boolean;
}

export function Footer({
  bgUrl,
  introTitle,
  introText,
  ctaTitle,
  ctaButton1Label,
  ctaButton1Href,
  ctaButton2Label,
  ctaButton2Href,
  navTechnology,
  navCompany,
  navPartnerships,
  navResources,
  navSocial,
  navLegal,
}: {
  bgUrl: string;
  introTitle: string;
  introText: string | React.ReactNode;
  ctaTitle: string;
  ctaButton1Label: string;
  ctaButton1Href: string;
  ctaButton2Label: string;
  ctaButton2Href: string;
  navTechnology: LinkProps[];
  navCompany: LinkProps[];
  navPartnerships: LinkProps[];
  navResources: LinkProps[];
  navSocial: LinkProps[];
  navLegal: LinkProps[];
}) {
  return (
    <>
      <footer className={cx("container")}>
        {bgUrl && typeof bgUrl === "string" && (
          <Image
            className={cx("bg")}
            src={bgUrl}
            fill
            alt=""
            aria-hidden={true}
            priority={false}
            sizes="100vw"
          />
        )}
        <div className={cx("inner")}>
          <div className={cx("content")}>
            <h2 className={cx("intro-title")}>{introTitle}</h2>
            <div className={cx("intro-text")}>{introText}</div>
            <div className={cx("cta-wrap")}>
              <h3 className={cx("cta-title")}>{ctaTitle}</h3>
              <div className={cx("cta-buttons")}>
                <Button href={ctaButton1Href} variant="primary">
                  {ctaButton1Label}
                </Button>
                <Button href={ctaButton2Href} variant="secondary-light">
                  {ctaButton2Label}
                </Button>
              </div>
            </div>
          </div>
          <div className={cx("nav-glass")}>
            <div className={cx("nav-content")}>
              <div className={cx("form")}>
                <NewsletterForm />
              </div>
              <div className={cx("nav-col")}>
                {[
                  { title: "Technology", items: navTechnology },
                  { title: "Company", items: navCompany },
                  { title: "Partnerships", items: navPartnerships },
                  { title: "Resources", items: navResources },
                ].map((section) => (
                  <div key={section.title} className={cx("nav-list-wrap")}>
                    <h3 className={cx("nav-list-title")}>{section.title}</h3>
                    <ul role="list" className={cx("nav-list")}>
                      {section.items.map((item) => (
                        <li key={item.name}>
                          <a
                            href={item.href}
                            target={item.newWindow ? "_blank" : "_self"}
                            rel={
                              item.newWindow ? "noopener noreferrer" : undefined
                            }
                          >
                            {item.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                <div className={cx("nav-list-wrap", "is-social")}>
                  <h3 className={cx("nav-list-title")}>Social</h3>
                  <ul role="list" className={cx("nav-list")}>
                    {navSocial.map((item) => (
                      <li key={item.name}>
                        <a
                          href={item.href}
                          target={item.newWindow ? "_blank" : "_self"}
                          rel={
                            item.newWindow ? "noopener noreferrer" : undefined
                          }
                        >
                          {item.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={cx("nav-list-wrap", "is-legal")}>
                  <h3 className={cx("nav-list-title")}>Legal</h3>
                  <ul role="list" className={cx("nav-list")}>
                    {navLegal.map((item) => (
                      <li key={item.name}>
                        <a
                          href={item.href}
                          target={item.newWindow ? "_blank" : "_self"}
                          rel={
                            item.newWindow ? "noopener noreferrer" : undefined
                          }
                        >
                          {item.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className={cx("logo")}>
              <NLXLogo />
            </div>
          </div>
        </div>
      </footer>

      <Script
        id="nlx-touchpoint"
        strategy="afterInteractive"
        type="module"
        dangerouslySetInnerHTML={{
          __html: `
          import {create, html, analyzePageForms} from "https://unpkg.com/@nlxai/touchpoint-ui/lib/index.js?module";

          async function initNLXTouchpoint() {
            if (Cookiebot.consent.preferences) {
              const conversationId = crypto.randomUUID();
              const userId = crypto.randomUUID();

              const CustomLaunchButton = ({className, onClick}) => {
                return html\`
                  <div className=\${className} style=\${{ width: "56px", height: "56px" }}>
                    <Ripple style=\${{ color: "rgba(0, 64, 255, 0.2)" }} className="rounded-outer" />
                    <button
                      className="relative rounded-outer p-2 after:content-[''] after:rounded-outer after:absolute after:inset-0 after:pointer-events-none hover:after:bg-primary-10"
                      style=\${{ color: "#fff", backgroundColor: "rgba(0, 64, 255, 1)" }}
                      onClick=\${onClick}
                    >
                      <svg fill="currentColor" viewBox="0 0 24 24" width="100%" height="100%"><path d="M13.556 5.348c-1.276 1.277-1.07 3.554.463 5.086.319.319.538.7.658 1.104a2.629 2.629 0 0 1-.288 2.13 2.6 2.6 0 0 1-.37.464 2.615 2.615 0 0 1-2.527.677l-.02-.005c-2.085-.55-4.15.409-4.615 2.148a1.961 1.961 0 1 1-1.45-2.419v-.001l.063.017c1.743.468 3.612-.85 4.172-2.943l.02-.068c.12-.404.339-.785.658-1.104l.012-.013a.86.86 0 0 1 .021-.021c1.502-1.531 1.697-3.784.43-5.052a1.961 1.961 0 1 1 2.819-.047v.002l-.046.045ZM19.327 6.832a1.275 1.275 0 1 1 0 2.55 1.275 1.275 0 0 1 0-2.55ZM21.324 16.475a1.994 1.994 0 1 0-3.988 0 1.994 1.994 0 0 0 3.988 0ZM12.102 19.32c.723 0 1.308.6 1.308 1.34 0 .74-.585 1.34-1.308 1.34-.722 0-1.307-.6-1.307-1.34 0-.74.585-1.34 1.307-1.34ZM6.284 8.14c0-.723-.6-1.308-1.34-1.308-.74 0-1.34.585-1.34 1.307 0 .723.6 1.308 1.34 1.308.74 0 1.34-.585 1.34-1.308Z"/></svg>
                    </button>
                  </div>
                \`;
              };

              const touchpoint = await create({
                config: {
                  applicationUrl: "https://apps.nlx.ai/c/A2H4U7RkXYHOMi7cDLQWf/gLYWMdVHBxjEOOPLv9ocX",
                  headers: {
                    "nlx-api-key": "0NWJjleNguV4Q9lqnHLwjtwR"
                  },
                  bidirectional: true,
                  languageCode: "en-US",
                  userId,
                  conversationId
                },
                launchIcon: CustomLaunchButton,
                input: "voiceMini",
              });

              let pageForms = analyzePageForms();

              // Function to refresh page context
              const refreshPageContext = () => {
                pageForms = analyzePageForms();
                touchpoint.conversationHandler.sendContext({ 
                  "nlx:vpContext": {
                    url: window.location.href,
                    fields: pageForms.context,
                    destinations: ["/about", "/platform", "/pricing", "/home", "/contact"]
                  }
                });
                console.log('Page context refreshed for:', window.location.href);
              };

              // SPA Navigation function using Next.js router
              const navigateToPage = (destination) => {
                // Check if destination is the same as current page
                if (window.location.pathname === destination) {
                  console.log('Already on destination page:', destination);
                  return;
                }

                // Use Next.js router for SPA navigation
                if (window.__NEXT_ROUTER__) {
                  window.__NEXT_ROUTER__.push(destination);
                } else if (window.next && window.next.router) {
                  window.next.router.push(destination);
                } else {
                  // Fallback: try to find Next.js router in global scope
                  const router = window.router || window.__next_router__;
                  if (router && router.push) {
                    router.push(destination);
                  } else {
                    // Last resort: use pushState for SPA-like navigation
                    window.history.pushState({}, '', destination);
                    // Trigger a popstate event to notify the app
                    window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
                  }
                }

                // Refresh context after navigation
                setTimeout(refreshPageContext, 100);
              };

              const handleNavigationCommand = (action, destination) => {
                console.log('Action:', action);
                console.log('Destination:', destination);

                switch (action) {
                  case 'page_next':
                    // Use browser's forward navigation
                    window.history.forward();
                    // Refresh context after navigation
                    setTimeout(refreshPageContext, 100);
                    break;
                  case 'page_previous':
                    // Use browser's back navigation
                    window.history.back();
                    // Refresh context after navigation
                    setTimeout(refreshPageContext, 100);
                    break;
                  case 'page_custom':
                    // Navigate to specific page using SPA navigation
                    if (destination) {
                      navigateToPage(destination);
                    }
                    break;
                  default:
                    console.log('Unknown navigation action:', action);
                }
              };

              const handleInputCommand = (fields) => {
                if (!pageForms?.formElements) {
                  return;
                }

                fields?.forEach((field) => {
                  if (pageForms?.formElements[field?.id]) {
                    console.log(\`Setting value for field \${field.id}:\`, field.value);
                    const element = pageForms.formElements[field.id];
                    
                    // Simply set the value directly
                    element.value = field.value;
                  } else {
                    console.warn(\`Form element with id \${field.id} not found\`);
                  }
                });
              };

              const handleCustomCommand = (action, payload) => {
                console.log('custom command:', action, payload);
              };

              const handleVoicePlusCommand = (command) => {
                const { classification, action, destination, payload } = command;

                switch (classification) {
                  case 'navigation':
                    handleNavigationCommand(action, destination);
                    break;
                  case 'input':
                    handleInputCommand(command?.fields);
                    break;
                  case 'custom':
                    handleCustomCommand(action, payload);
                    break;
                  default:
                    console.log('Unknown command:', classification);
                }
              };

              // Listen for browser navigation events to refresh context
              window.addEventListener('popstate', () => {
                setTimeout(refreshPageContext, 100);
              });

              // Listen for pushstate/replacestate events (for SPA navigation)
              const originalPushState = history.pushState;
              const originalReplaceState = history.replaceState;

              history.pushState = function() {
                originalPushState.apply(history, arguments);
                setTimeout(refreshPageContext, 100);
              };

              history.replaceState = function() {
                originalReplaceState.apply(history, arguments);
                setTimeout(refreshPageContext, 100);
              };

              // Initial context setup
              refreshPageContext();

              touchpoint.conversationHandler.addEventListener("voicePlusCommand", (command) => {
                console.log({ command });
                handleVoicePlusCommand(command);
              });

              // Store reference to touchpoint for global access
              window.nlxTouchpoint = touchpoint;
              
              // Expose global function to open touchpoint
              window.openNLXTouchpoint = () => {
                // First try to find and click the launch button
                const launchButton = document.querySelector('.rounded-outer button');
                if (launchButton) {
                  launchButton.click();
                  return;
                }
                
                // Try to set expanded property to true
                if (touchpoint.hasOwnProperty('expanded')) {
                  touchpoint.expanded = true;
                  return;
                }
                
                // Try methods on conversationHandler
                if (touchpoint.conversationHandler) {
                  const handler = touchpoint.conversationHandler;
                  if (handler.open) {
                    handler.open();
                  } else if (handler.show) {
                    handler.show();
                  } else if (handler.start) {
                    handler.start();
                  } else {
                    console.error('No valid method found on conversationHandler. Available methods:', Object.keys(handler));
                  }
                } else {
                  console.error('No valid method found to open touchpoint. Available methods:', Object.keys(touchpoint));
                }
              };
            }
          }

          await initNLXTouchpoint();

          window.addEventListener('CookiebotOnAccept', function() {
            initNLXTouchpoint();
          });
        `,
        }}
      />
    </>
  );
}
